import { useState } from 'react';
import type { Quadrant, Task } from '../types';
import { QUADRANTS, QUADRANT_LABELS } from '../types';

interface Props {
  task: Task;
  readOnly: boolean;
  onUpdate: (id: string, patch: Partial<Pick<Task, 'title' | 'notes' | 'quadrant'>>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onMove: (id: string, to: Quadrant) => Promise<void>;
}

export function TaskCard({ task, readOnly, onUpdate, onDelete, onMove }: Props) {
  const [editing, setEditing] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [draftTitle, setDraftTitle] = useState(task.title);
  const [draftNotes, setDraftNotes] = useState(task.notes ?? '');
  const [busy, setBusy] = useState(false);

  if (readOnly) {
    return (
      <div className="task-card task-card--ro">
        <div className="task-title">{task.title}</div>
        {task.notes && <div className="task-notes">{task.notes}</div>}
      </div>
    );
  }

  if (editing) {
    const save = async () => {
      const trimmed = draftTitle.trim();
      if (!trimmed) return;
      setBusy(true);
      try {
        await onUpdate(task.id, {
          title: trimmed,
          notes: draftNotes.trim() || null,
        });
        setEditing(false);
      } finally {
        setBusy(false);
      }
    };

    return (
      <div className="task-card task-card--editing">
        <input
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          placeholder="Task title"
          autoFocus
        />
        <textarea
          value={draftNotes}
          onChange={(e) => setDraftNotes(e.target.value)}
          placeholder="Notes (optional)"
          rows={2}
        />
        <div className="task-actions">
          <button type="button" onClick={save} disabled={busy}>Save</button>
          <button type="button" onClick={() => { setEditing(false); setDraftTitle(task.title); setDraftNotes(task.notes ?? ''); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="task-card">
      <div className="task-title">{task.title}</div>
      {task.notes && <div className="task-notes">{task.notes}</div>}

      <div className="task-actions">
        <button type="button" onClick={() => setEditing(true)}>Edit</button>
        <button type="button" onClick={() => setShowMove((v) => !v)}>Move</button>
        <button
          type="button"
          className="danger"
          onClick={async () => {
            if (!confirm(`Delete "${task.title}"?`)) return;
            await onDelete(task.id);
          }}
        >
          Delete
        </button>
      </div>

      {showMove && (
        <div className="task-move">
          {QUADRANTS.filter((q) => q !== task.quadrant).map((q) => (
            <button
              key={q}
              type="button"
              onClick={async () => {
                await onMove(task.id, q);
                setShowMove(false);
              }}
            >
              → {QUADRANT_LABELS[q].title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
